import React from 'react';
import { Package } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { getTranslatedField } from '../utils/translationUtils';
import { useI18n } from '../i18n/i18nContext';

type PackagePackOption = NonNullable<Package['packs']>[number];

interface PackagePacksTableProps {
  items: Package[];
  language: string;
  onPurchase: (pkg: Package, pack: PackagePackOption) => void;
  purchaseLabel: string;
  promotionTitle?: string;
}

const PackagePacksTable: React.FC<PackagePacksTableProps> = ({
  items,
  language,
  onPurchase,
  purchaseLabel,
  promotionTitle,
}) => {
  const { t } = useI18n();
  const hasDiscount = items.some((pkg) =>
    (pkg.packs || []).some((pack) => (pack.original_price || 0) > (pack.price || 0))
  );
  const discountColumnLabel = promotionTitle || t.pricing.openingDealSessionBundle;

  const packColumns = Array.from(
    new Set(
      items.flatMap((pkg) =>
        (pkg.packs || [])
          .map((pack) => pack.package_count)
          .filter((count): count is number => typeof count === 'number' && count > 0)
      )
    )
  ).sort((a, b) => a - b);

  const getPackageName = (pkg: Package): string => {
    return (getTranslatedField(pkg, 'name', language) as string) || pkg.name || '';
  };

  const getServicesCount = (pkg: Package): number => {
    return pkg.services?.length || 0;
  };

  const findPackByCount = (pkg: Package, packageCount: number): PackagePackOption | undefined => {
    return (pkg.packs || []).find((pack) => pack.package_count === packageCount);
  };

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full table-fixed text-left text-sm">
          <thead className="border-b border-blue-300 bg-blue-100 tracking-wider">
            <tr>
              <th scope="col" className="w-2/5 px-4 py-4 font-semibold text-gray-700">
                {t.pricing.package}
              </th>
              <th
                scope="col"
                className="w-24 px-4 py-4 text-center font-semibold text-gray-700 first-letter:uppercase"
              >
                {t.booking.minutes}
              </th>
              {packColumns.map((count) => (
                <th
                  key={`pack-col-${count}`}
                  scope="col"
                  className="px-4 py-4 text-center font-semibold text-gray-700"
                >
                  {count} {count === 1 ? t.pricing.package : t.pricing.packages}
                  {hasDiscount && (
                    <div className="mt-1 text-xs font-semibold text-green-600">
                      {discountColumnLabel}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.map((pkg, index) => {
              return (
                <tr
                  key={pkg.id}
                  className={`border-b border-blue-200 ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`}
                >
                  <td className="w-2/5 px-4 py-4">
                    <div className="font-medium text-gray-900">
                      {getPackageName(pkg)} ({getServicesCount(pkg)})
                    </div>
                  </td>
                  <td className="w-24 px-4 py-4 text-center text-gray-700">
                    {pkg.duration_minutes || 0}
                  </td>
                  {packColumns.map((count) => {
                    const pack = findPackByCount(pkg, count);
                    return (
                      <td key={`${pkg.id}-${count}`} className="px-4 py-4 text-center">
                        {pack ? (
                          <div className="flex items-center justify-center gap-3">
                            <div className="flex flex-col items-center">
                              {hasDiscount && (pack.original_price || 0) > (pack.price || 0) ? (
                                <>
                                  <span className="text-sm text-gray-400 line-through">
                                    {formatPrice(pack.original_price || 0)}
                                  </span>
                                  <span className="font-semibold text-gray-900">
                                    {formatPrice(pack.price || 0)}
                                  </span>
                                </>
                              ) : (
                                <span className="font-semibold text-gray-900">
                                  {formatPrice(pack.price || 0)}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => onPurchase(pkg, pack)}
                              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                            >
                              {purchaseLabel}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        {items.map((pkg, index) => {
          return (
            <div
              key={pkg.id}
              className={`border-b border-blue-200 p-4 ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="font-medium text-gray-900">
                  {getPackageName(pkg)} ({getServicesCount(pkg)})
                </div>
                <div className="text-sm text-gray-600">
                  {pkg.duration_minutes || 0} {t.booking.minutes || 'min'}
                </div>
              </div>

              <div className="space-y-2">
                {(pkg.packs || [])
                  .slice()
                  .sort((a, b) => a.package_count - b.package_count)
                  .map((pack) => (
                    <div
                      key={`${pkg.id}-${pack.package_count}`}
                      className="flex items-center justify-between rounded-md border border-blue-100 bg-blue-50 px-3 py-2"
                    >
                      <div className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">
                          {pack.package_count} {'pckg'}
                        </span>
                        <span className="mx-2 text-gray-400">-</span>
                        {hasDiscount && (pack.original_price || 0) > (pack.price || 0) ? (
                          <>
                            <span className="text-xs text-gray-400 line-through">
                              {formatPrice(pack.original_price || 0)}
                            </span>
                            <span className="mx-1 text-gray-400">/</span>
                            <span className="font-semibold text-gray-900">
                              {formatPrice(pack.price || 0)}
                            </span>
                          </>
                        ) : (
                          <span className="font-semibold text-gray-900">
                            {formatPrice(pack.price || 0)}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => onPurchase(pkg, pack)}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                      >
                        {purchaseLabel}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PackagePacksTable;
